import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  Chip,
  Alert,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';

import { QuoteQuestion } from '@/api/endpoints/quote/useGenerateQuoteQuestions';
import { AlertTriangleIcon } from '@/assets/icons/AlertTriangleIcon';
import { CheckCircleIcon } from '@/assets/icons/CheckCircleIcon';
import { ClipboardCheckIcon } from '@/assets/icons/ClipboardCheckIcon';
import { ClockIcon } from '@/assets/icons/ClockIcon';
import { EditIcon } from '@/assets/icons/EditIcon';
import { Phone01Icon } from '@/assets/icons/Phone01Icon';
import { StoredQuoteAnswers } from '@/services/QuoteAnswerSessionService';

interface QuoteAnswersConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onProceedWithSavedAnswers: () => void;
  onEditAnswers: () => void;
  savedAnswers: StoredQuoteAnswers;
  vendorNames: string[];
}

const QuoteAnswersConfirmationDialog: React.FC<QuoteAnswersConfirmationDialogProps> = ({
  open,
  onClose,
  onProceedWithSavedAnswers,
  onEditAnswers,
  savedAnswers,
  vendorNames,
}) => {
  const formatAnswerValue = (question: QuoteQuestion, value: string | number): string => {
    if (value === null || value === undefined || value === '') {
      return 'Not answered';
    }

    // Handle date formatting
    if (question.input_type === 'date') {
      try {
        return dayjs(value as string).format('MMMM DD, YYYY');
      } catch {
        return value.toString();
      }
    }

    // Handle select options
    if (question.input_type === 'select') {
      return `"${value.toString()}"`;
    }

    // Handle number formatting
    if (question.input_type === 'number') {
      const num = Number(value);
      if (Number.isNaN(num)) return value.toString();

      // Add appropriate formatting for common number types
      if (
        question.text.toLowerCase().includes('price') ||
        question.text.toLowerCase().includes('cost') ||
        question.text.toLowerCase().includes('budget')
      ) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }).format(num);
      }

      if (
        question.text.toLowerCase().includes('square') ||
        question.text.toLowerCase().includes('sq')
      ) {
        return `${num.toLocaleString()} sq ft`;
      }

      return num.toLocaleString();
    }

    // Handle text - truncate very long responses and add quotes for clarity
    const text = value.toString().trim();
    if (text.length > 200) {
      return `"${text.substring(0, 200)}..."`;
    }

    return `"${text}"`;
  };

  const getTimeAgo = (timestamp: string): string => {
    const now = dayjs();
    const answerTime = dayjs(timestamp);
    const minutesAgo = now.diff(answerTime, 'minute');
    const hoursAgo = now.diff(answerTime, 'hour');

    if (minutesAgo < 1) {
      return 'just now';
    }
    if (minutesAgo < 60) {
      return `${minutesAgo} minute${minutesAgo > 1 ? 's' : ''} ago`;
    }
    if (hoursAgo < 24) {
      return `${hoursAgo} hour${hoursAgo > 1 ? 's' : ''} ago`;
    }
    return answerTime.format('MMM DD, YYYY [at] h:mm A');
  };

  // Count answered questions
  const totalQuestions = savedAnswers.questions.length;
  const answeredQuestions = savedAnswers.questions.filter(
    q => savedAnswers.answers[q.id] !== undefined && savedAnswers.answers[q.id] !== ''
  ).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            maxHeight: '95vh',
            height: 'auto',
            minHeight: '60vh',
          },
        },
      }}
    >
      <DialogTitle>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleIcon width={24} height={24} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Use Previous Answers?
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            We found answers from your previous search. You can use them to start calling
            immediately or review and edit them first.
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pb: 1, overflowY: 'visible' }}>
        <Stack spacing={3}>
          {/* Session info */}
          <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Service: {savedAnswers.serviceType}
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <ClipboardCheckIcon width={16} height={16} />
                        <Typography variant="caption" color="text.secondary">
                          {answeredQuestions} of {totalQuestions} questions answered
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <ClockIcon width={16} height={16} />
                        <Typography variant="caption" color="text.secondary">
                          Answered {getTimeAgo(savedAnswers.timestamp)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>

                {/* Vendor chips */}
                {vendorNames.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 1, display: 'block' }}
                    >
                      Calling these vendors:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {vendorNames.map(name => (
                        <Chip
                          key={name}
                          label={name}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Divider />

          {/* Previous answers */}
          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Your Previous Answers
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {answeredQuestions} of {totalQuestions} questions completed
              </Typography>
            </Stack>
            <Stack spacing={2} sx={{ overflowY: 'auto', pr: 1 }}>
              {savedAnswers.questions.map((question, index) => {
                const answer = savedAnswers.answers[question.id];
                const hasAnswer = answer !== undefined && answer !== '';

                return (
                  <Card
                    key={question.id}
                    variant="outlined"
                    sx={{
                      bgcolor: hasAnswer ? 'background.paper' : 'action.hover',
                      borderColor: hasAnswer ? 'divider' : 'warning.main',
                      opacity: hasAnswer ? 1 : 0.7,
                    }}
                  >
                    <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                      <Stack direction="row" spacing={2}>
                        <Typography
                          variant="caption"
                          sx={{
                            minWidth: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: hasAnswer ? 'primary.main' : 'text.disabled',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                          }}
                        >
                          {index + 1}
                        </Typography>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              mb: 1,
                              color: hasAnswer ? 'text.primary' : 'text.secondary',
                            }}
                          >
                            {question.text}
                            {question.required && (
                              <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                                *
                              </Typography>
                            )}
                          </Typography>

                          {/* Answer Display */}
                          <Box
                            sx={{
                              bgcolor: hasAnswer ? 'primary.50' : 'warning.50',
                              border: 1,
                              borderColor: hasAnswer ? 'primary.200' : 'warning.200',
                              borderRadius: 1,
                              px: 2,
                              py: 1.5,
                              mt: 1,
                              minHeight: '48px',
                              display: 'flex',
                              alignItems: 'flex-start',
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="flex-start"
                              spacing={1}
                              sx={{ width: '100%' }}
                            >
                              <Box sx={{ mt: 0.25, flexShrink: 0 }}>
                                {hasAnswer ? (
                                  <CheckCircleIcon fontSize="small" color="primary" />
                                ) : (
                                  <AlertTriangleIcon fontSize="small" color="warning" />
                                )}
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    color: hasAnswer ? 'primary.700' : 'warning.700',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    display: 'block',
                                    mb: 0.5,
                                  }}
                                >
                                  {hasAnswer ? 'Answer:' : 'Missing:'}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: hasAnswer ? 'primary.900' : 'warning.800',
                                    fontWeight: hasAnswer ? 500 : 400,
                                    fontStyle: hasAnswer ? 'normal' : 'italic',
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {formatAnswerValue(question, answer)}
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </Box>

          {/* Info message */}
          <Alert severity="info" icon={<Phone01Icon width={20} height={20} />}>
            <Typography variant="body2">
              These answers will be used to get accurate quotes from vendors. You can proceed with
              these answers or edit them before calling.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={onEditAnswers}
          variant="outlined"
          startIcon={<EditIcon width={20} height={20} />}
        >
          Review & Edit
        </Button>
        <Button
          onClick={onProceedWithSavedAnswers}
          variant="contained"
          startIcon={<Phone01Icon width={20} height={20} />}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          }}
        >
          Start Calls Now
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuoteAnswersConfirmationDialog;
